export interface FaqItem {
  id?: string
  question: string
  answer: string
  position?: number
  isVisible?: boolean
}

interface FaqPayload {
  title: string
  items: FaqItem[]
}

const FAQ_BLOCK_REGEX = /<div[^>]*(?:class=["'][^"']*wft-faq-block[^"']*["'][^>]*data-wft-faq=["']([^"']+)["']|data-wft-faq=["']([^"']+)["'][^>]*class=["'][^"']*wft-faq-block[^"']*["'])[^>]*>.*?<\/div>(?:\s*<\/div>)?(?:<p><\/p>)?/gis

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const decodeFaqPayload = (value?: string | null): FaqPayload => {
  if (!value) return { title: 'FAQ', items: [] }
  try {
    let decoded = value
    for (let index = 0; index < 2; index++) {
      const nextDecoded = decodeURIComponent(decoded)
      if (nextDecoded === decoded) break
      decoded = nextDecoded
    }
    const parsed = JSON.parse(decoded)
    const rawItems = Array.isArray(parsed) ? parsed : parsed?.items
    if (!Array.isArray(rawItems)) return { title: 'FAQ', items: [] }
    const items = rawItems
      .map((item, index) => ({
        id: item?.id || `faq-${index + 1}`,
        question: typeof item?.question === 'string' ? item.question.trim() : '',
        answer: typeof item?.answer === 'string' ? item.answer.trim() : '',
        position: typeof item?.position === 'number' ? item.position : index + 1,
        isVisible: item?.isVisible !== false,
      }))
      .filter(item => item.question && item.answer && item.isVisible !== false)
    return {
      title: typeof parsed?.title === 'string' && parsed.title.trim() ? parsed.title.trim() : 'FAQ',
      items,
    }
  } catch {
    return { title: 'FAQ', items: [] }
  }
}

export const decodeFaqItems = (value?: string | null): FaqItem[] => decodeFaqPayload(value).items

export const extractFaqsFromHtml = (html?: string | null): FaqItem[] => {
  if (!html) return []
  const items: FaqItem[] = []
  if (import.meta.client) {
    const template = document.createElement('template')
    template.innerHTML = html
    template.content.querySelectorAll<HTMLElement>('.wft-faq-block[data-wft-faq], [data-type="faq-block"][data-wft-faq]').forEach((element) => {
      items.push(...decodeFaqItems(element.getAttribute('data-wft-faq')))
    })
    return items
  }

  let match: RegExpExecArray | null
  while ((match = FAQ_BLOCK_REGEX.exec(html)) !== null) {
    items.push(...decodeFaqItems(match[1] || match[2]))
  }
  FAQ_BLOCK_REGEX.lastIndex = 0
  return items
}

export const renderFaqBlocksInHtml = (html?: string | null): string => {
  if (!html) return ''
  const renderFaqAccordion = (encoded?: string | null): string => {
    const payload = decodeFaqPayload(encoded)
    if (!payload.items.length) return ''
    const rows = payload.items.map((item, index) => `
      <details class="wft-faq-accordion__item" ${index === 0 ? 'open' : ''}>
        <summary class="wft-faq-accordion__question">${escapeHtml(item.question)}</summary>
        <div class="wft-faq-accordion__answer">${item.answer}</div>
      </details>`).join('')
    return `<section class="wft-faq-block__content">
      <h2 class="wft-faq-block__title">${escapeHtml(payload.title)}</h2>
      <div class="wft-faq-accordion">${rows}
      </div>
    </section>`
  }

  if (import.meta.client) {
    const template = document.createElement('template')
    template.innerHTML = html
    template.content.querySelectorAll<HTMLElement>('.wft-faq-block[data-wft-faq], [data-type="faq-block"][data-wft-faq]').forEach((element) => {
      const accordion = renderFaqAccordion(element.getAttribute('data-wft-faq'))
      if (accordion) {
        const wrapper = document.createElement('template')
        wrapper.innerHTML = accordion
        element.replaceWith(wrapper.content)
      } else {
        element.remove()
      }
    })
    return template.innerHTML
  }

  return html.replace(FAQ_BLOCK_REGEX, (_, encodedBeforeClass, encodedAfterClass) => renderFaqAccordion(encodedBeforeClass || encodedAfterClass))
}

export const buildFaqPageSchema = (items: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
})

export const dedupeFaqItems = (items: FaqItem[]): FaqItem[] => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = `${item.id || ''}::${item.question}::${item.answer}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
